import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  type ComponentProps,
  type PropsWithChildren,
} from "react";

import {
  AGENT_EMPTY_ENTRANCE_TIMING,
  agentEmptyEntranceContentVariants,
  agentEmptyEntranceGradientVariants,
  agentEmptyEntranceMotionProps,
  motion,
  useAgentReducedMotion,
} from "./lib/motion";

export { AGENT_EMPTY_ENTRANCE_TIMING, agentEmptyEntranceTotalMs } from "./lib/motion";

type AgentEmptyEntranceContextValue = {
  active: boolean;
  reducedMotion: boolean;
  onComplete: () => void;
};

const AgentEmptyEntranceContext = createContext<AgentEmptyEntranceContextValue | null>(null);

const useAgentEmptyEntranceContext = (): AgentEmptyEntranceContextValue => {
  const context = useContext(AgentEmptyEntranceContext);
  if (!context) {
    throw new Error("Agent empty entrance slots must render inside AgentEmptyEntrance.");
  }
  return context;
};

export type AgentEmptyEntranceProps = PropsWithChildren<{
  /** True while the one-time full-page empty entrance sequence should run. */
  active: boolean;
  /** Called once the last staggered suggestion finishes animating in. */
  onComplete: () => void;
}>;

/** Coordinates the one-time full-page empty Agent entrance sequence. */
export const AgentEmptyEntrance = ({ active, children, onComplete }: AgentEmptyEntranceProps) => {
  const reducedMotion = useAgentReducedMotion();

  useLayoutEffect(() => {
    if (active && reducedMotion) {
      onComplete();
    }
  }, [active, onComplete, reducedMotion]);

  const value = useMemo(
    () => ({
      active,
      onComplete,
      reducedMotion,
    }),
    [active, onComplete, reducedMotion],
  );

  return (
    <AgentEmptyEntranceContext.Provider value={value}>
      <div
        className="contents"
        data-agent-empty-entrance-root={active ? "active" : "idle"}
        data-testid="agent-empty-entrance-root"
      >
        {children}
      </div>
    </AgentEmptyEntranceContext.Provider>
  );
};

const contentTransition = (delayMs: number, shouldStagger: boolean) => ({
  delay: shouldStagger ? delayMs / 1000 : 0,
  duration: shouldStagger ? AGENT_EMPTY_ENTRANCE_TIMING.contentDurationMs / 1000 : 0,
  ease: "easeOut" as const,
});

export type AgentEmptyEntranceMeshProps = ComponentProps<typeof motion.img>;

/** Fades the mesh gradient in first during the empty full-page entrance. */
export const AgentEmptyEntranceMesh = ({
  className,
  style,
  ...props
}: AgentEmptyEntranceMeshProps) => {
  const { active, reducedMotion } = useAgentEmptyEntranceContext();
  const shouldAnimate = active && !reducedMotion;

  return (
    <motion.img
      {...agentEmptyEntranceMotionProps}
      {...props}
      animate="enter"
      className={className}
      data-agent-empty-entrance={shouldAnimate ? "gradient" : undefined}
      data-testid="agent-empty-entrance-gradient"
      initial={shouldAnimate ? "hidden" : false}
      style={style}
      transition={
        shouldAnimate
          ? {
              delay: AGENT_EMPTY_ENTRANCE_TIMING.gradientDelayMs / 1000,
              duration: AGENT_EMPTY_ENTRANCE_TIMING.gradientDurationMs / 1000,
              ease: "easeOut",
            }
          : { duration: 0 }
      }
      variants={agentEmptyEntranceGradientVariants}
    />
  );
};

type ContentSlotProps = PropsWithChildren<{
  className?: string;
}>;

const AgentEmptyEntranceGreeting = ({ children, className }: ContentSlotProps) => {
  const { active, reducedMotion } = useAgentEmptyEntranceContext();
  const shouldAnimate = active && !reducedMotion;

  return (
    <motion.div
      className={className}
      data-agent-empty-entrance={shouldAnimate ? "greeting" : undefined}
      data-testid="agent-empty-entrance-greeting"
      {...agentEmptyEntranceMotionProps}
      animate="enter"
      initial={shouldAnimate ? "hidden" : false}
      transition={contentTransition(AGENT_EMPTY_ENTRANCE_TIMING.greetingDelayMs, shouldAnimate)}
      variants={agentEmptyEntranceContentVariants(AGENT_EMPTY_ENTRANCE_TIMING.contentOffsetPx)}
    >
      {children}
    </motion.div>
  );
};

const AgentEmptyEntranceComposer = ({ children, className }: ContentSlotProps) => {
  const { active, reducedMotion } = useAgentEmptyEntranceContext();
  const shouldAnimate = active && !reducedMotion;

  return (
    <motion.div
      className={className}
      data-agent-empty-entrance={shouldAnimate ? "composer" : undefined}
      data-testid="agent-empty-entrance-composer"
      {...agentEmptyEntranceMotionProps}
      animate="enter"
      initial={shouldAnimate ? "hidden" : false}
      transition={contentTransition(AGENT_EMPTY_ENTRANCE_TIMING.composerDelayMs, shouldAnimate)}
      variants={agentEmptyEntranceContentVariants(AGENT_EMPTY_ENTRANCE_TIMING.composerOffsetPx)}
    >
      {children}
    </motion.div>
  );
};

export type AgentEmptyEntranceSuggestionProps = ContentSlotProps & {
  index: number;
  isLast: boolean;
};

const AgentEmptyEntranceSuggestion = ({
  children,
  className,
  index,
  isLast,
}: AgentEmptyEntranceSuggestionProps) => {
  const { active, onComplete, reducedMotion } = useAgentEmptyEntranceContext();
  const shouldAnimate = active && !reducedMotion;
  const delayMs =
    AGENT_EMPTY_ENTRANCE_TIMING.suggestionsBaseDelayMs +
    index * AGENT_EMPTY_ENTRANCE_TIMING.suggestionsStaggerMs;

  return (
    <motion.div
      // `display: contents` (used elsewhere in this file for non-animated layout wrappers) strips
      // the box that `opacity`/`transform` animate: the browser applies neither property to a
      // contents element, so motion reaches its final inline styles with no visible transition.
      // `Suggestions` is a `flex flex-wrap` row, so a plain block-level wrapper is already a valid,
      // correctly sized flex item here — the same pattern the Greeting and Composer slots use.
      className={className}
      data-agent-empty-entrance={shouldAnimate ? "suggestion" : undefined}
      data-testid="agent-empty-entrance-suggestion"
      {...agentEmptyEntranceMotionProps}
      animate="enter"
      initial={shouldAnimate ? "hidden" : false}
      onAnimationComplete={() => {
        if (shouldAnimate && isLast) {
          onComplete();
        }
      }}
      transition={contentTransition(delayMs, shouldAnimate)}
      variants={agentEmptyEntranceContentVariants(AGENT_EMPTY_ENTRANCE_TIMING.contentOffsetPx)}
    >
      {children}
    </motion.div>
  );
};

export type AgentEmptyEntranceCompound = typeof AgentEmptyEntrance & {
  Mesh: typeof AgentEmptyEntranceMesh;
  Greeting: typeof AgentEmptyEntranceGreeting;
  Composer: typeof AgentEmptyEntranceComposer;
  Suggestion: typeof AgentEmptyEntranceSuggestion;
};

AgentEmptyEntrance.Mesh = AgentEmptyEntranceMesh;
AgentEmptyEntrance.Greeting = AgentEmptyEntranceGreeting;
AgentEmptyEntrance.Composer = AgentEmptyEntranceComposer;
AgentEmptyEntrance.Suggestion = AgentEmptyEntranceSuggestion;
